import { Injectable, signal } from '@angular/core';
import { marked } from 'marked';

/**
 * Markdown naar html, met een cache zodat change detection niet bij elke doorloop opnieuw parst.
 */
@Injectable({ providedIn: 'root' })
export class MarkdownService {
	/**
	 * Maximaal aantal entries in de cache. Als er meer entries zijn, wordt de oudste verwijderd om de
	 * grootte van de cache te beperken.
	 */
	private static readonly MAX_ENTRIES = 100;

	private readonly cache = signal(new Map<string, string>());

	/** Volledige markdown, inclusief blokelementen zoals alinea's en lijsten. */
	parse(text: string): string {
		return this.parseCached('block', text, () => marked.parse(text));
	}

	/**
	 * Alleen inline markdown: geen omsluitende `<p>`, zodat de tekst binnen een bestaande regel past.
	 * Voor notities, die als één regel worden getoond.
	 */
	parseInline(text: string): string {
		return this.parseCached('inline', text, () => marked.parseInline(text));
	}

	/**
	 * Geeft een lege string terug als marked een promise oplevert (wat gebeurt bij async markdown
	 * zoals afbeeldingen); zodra die resolvet wordt de cache bijgewerkt en rendert de component
	 * opnieuw met de juiste html.
	 */
	private parseCached(soort: string, text: string, parse: () => string | Promise<string>): string {
		const key = `${soort}:${text}`;
		const cache = this.cache();
		if (cache.has(key)) {
			return cache.get(key)!;
		}

		const result = parse();
		if (typeof result === 'string') {
			// Directe mutatie van de Map, geen signal-write: parse() wordt aangeroepen vanuit een
			// template-interpolatie, en Angular verbiedt signal-writes tijdens het renderen (NG0600).
			// De referentie in het signal verandert niet, maar niets leest de cache reactief uit — elke
			// aanroep gebruikt zijn eigen return-waarde — dus dat is hier onschadelijk.
			this.evictIfNeeded(cache.set(key, result));
			return result;
		}

		// Buiten de render-cyclus (microtask), dus hier mag de signal-write wel.
		result.then((html) =>
			this.cache.update((c) => {
				const next = new Map(c).set(key, html);
				this.evictIfNeeded(next);
				return next;
			})
		);
		return '';
	}

	private evictIfNeeded(cache: Map<string, string>) {
		if (cache.size <= MarkdownService.MAX_ENTRIES) return;

		const oudste = cache.keys().next().value;
		if (oudste !== undefined) cache.delete(oudste);
	}
}
